"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { TicketHallLogo } from "@/components/TicketHallLogo";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui/input-otp";
import { Eye, EyeOff, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type LoginSignupModalView = "login" | "register" | "verify-otp";

export interface LoginSignupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultView?: LoginSignupModalView;
  loading?: boolean;
  onSubmit?: (
    data: { email: string; password: string; name?: string },
    view: LoginSignupModalView
  ) => void;
  onVerifyOtp?: (email: string, code: string) => void;
  onViewChange?: (view: LoginSignupModalView) => void;
  onSocialLogin?: (provider: "google") => void;
  onForgotPassword?: () => void;
  pendingEmail?: string;
}

export default function LoginSignupModal({
  loading = false,
  open,
  onOpenChange,
  defaultView = "login",
  onSubmit,
  onVerifyOtp,
  onViewChange,
  onForgotPassword,
  pendingEmail = "",
}: LoginSignupModalProps) {
  const [view, setView] = React.useState<LoginSignupModalView>(defaultView);
  const [showPassword, setShowPassword] = React.useState(false);
  const [email, setEmail] = React.useState(pendingEmail);
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [remember, setRemember] = React.useState(false);
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [passwordMismatch, setPasswordMismatch] = React.useState(false);
  const [otpCode, setOtpCode] = React.useState("");

  // Load remembered email when modal opens or mounts
  React.useEffect(() => {
    if (open) {
      setView(defaultView);
      setShowPassword(false);
      setOtpCode("");
      const savedEmail = localStorage.getItem("ticket-hall-remember-email");
      if (savedEmail && !email) {
        setEmail(savedEmail);
        setRemember(true);
      }
    }
  }, [defaultView, open]);

  React.useEffect(() => {
    if (pendingEmail) {
      setEmail(pendingEmail);
    }
  }, [pendingEmail]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (view === "register" && password !== confirmPassword) {
      setPasswordMismatch(true);
      return;
    }

    if (remember && email) {
      localStorage.setItem("ticket-hall-remember-email", email);
    } else {
      localStorage.removeItem("ticket-hall-remember-email");
    }

    onSubmit?.(
      {
        email,
        password,
        name: view === "register" ? name : undefined,
      },
      view,
    );
  };

  const handleOtpSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (otpCode.length === 6) {
      onVerifyOtp?.(email, otpCode);
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={`fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity ${
            open ? "dialog-overlay-fade" : ""
          }`}
          style={{ touchAction: "manipulation" }}
        />
        <DialogPrimitive.Content
          className={`fixed inset-0 z-50 flex items-center justify-center max-sm:p-0 sm:p-4 outline-none ${
            open ? "dialog-fade" : ""
          }`}
          onOpenAutoFocus={(event) => event.preventDefault()}
          aria-describedby="login-signup-desc"
          style={{ touchAction: "manipulation" }}
        >
          <DialogPrimitive.Title className="sr-only">
            {view === "login"
              ? "Entrar na conta"
              : view === "register"
              ? "Criar conta"
              : "Verificar código de autenticação"}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description id="login-signup-desc" className="sr-only">
            {view === "login"
              ? "Acesse sua conta para comprar, acompanhar pedidos e muito mais."
              : view === "register"
              ? "Crie sua conta para começar a explorar eventos e ofertas."
              : "Digite o código de 6 dígitos enviado para seu e-mail."}
          </DialogPrimitive.Description>

          <div
            className="w-full h-full max-sm:min-h-[100dvh] max-sm:rounded-none max-sm:border-none sm:h-auto sm:max-h-[92dvh] sm:max-w-[400px] sm:rounded-2xl flex flex-col justify-between bg-card text-foreground border border-border/80 shadow-2xl overflow-y-auto transition-all p-6 max-sm:p-5 max-sm:py-6"
            style={{ touchAction: "manipulation" }}
          >
            {/* Top Bar */}
            <div className="flex items-center justify-between w-full flex-shrink-0 mb-2">
              <span className="text-xs text-muted-foreground font-medium select-none">
                Português (Brasil)
              </span>
              <DialogPrimitive.Close
                className="rounded-full p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition focus:outline-none"
                style={{ touchAction: "manipulation" }}
              >
                <X className="h-5 w-5" />
                <span className="sr-only">Fechar</span>
              </DialogPrimitive.Close>
            </div>

            {/* SCREEN VIEW: LOGIN & REGISTER */}
            {(view === "login" || view === "register") && (
              <>
                <div className="flex-1 flex flex-col justify-center items-center my-auto py-4 space-y-6 w-full">
                  {/* Logo */}
                  <div className="flex flex-col items-center justify-center w-full py-2">
                    <TicketHallLogo size="xl" />
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3.5 w-full" style={{ touchAction: "manipulation" }}>
                    {view === "register" && (
                      <div className="relative flex items-center h-12 w-full rounded-xl border border-border/80 bg-secondary/30 px-4 transition-all focus-within:border-primary focus-within:bg-background">
                        <input
                          id="signup-name"
                          type="text"
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                          placeholder="Nome completo"
                          className="w-full bg-transparent p-0 text-[16px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 border-none shadow-none"
                          style={{ fontSize: "16px", touchAction: "manipulation" }}
                          autoComplete="name"
                          required
                        />
                      </div>
                    )}

                    <div className="relative flex items-center h-12 w-full rounded-xl border border-border/80 bg-secondary/30 px-4 transition-all focus-within:border-primary focus-within:bg-background">
                      <input
                        id="login-email"
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="E-mail (ex: seu@email.com)"
                        className="w-full bg-transparent p-0 text-[16px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 border-none shadow-none"
                        style={{ fontSize: "16px", touchAction: "manipulation" }}
                        autoComplete="email"
                        required
                      />
                    </div>

                    <div className="relative flex items-center h-12 w-full rounded-xl border border-border/80 bg-secondary/30 px-4 transition-all focus-within:border-primary focus-within:bg-background">
                      <input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(event) => {
                          const val = event.target.value;
                          setPassword(val);
                          if (passwordMismatch && confirmPassword === val) {
                            setPasswordMismatch(false);
                          }
                        }}
                        placeholder="Senha"
                        className="w-full bg-transparent p-0 pr-8 text-[16px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 border-none shadow-none"
                        style={{ fontSize: "16px", touchAction: "manipulation" }}
                        autoComplete={view === "login" ? "current-password" : "new-password"}
                        required
                      />
                      <button
                        type="button"
                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                        className="absolute right-3.5 rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                        style={{ touchAction: "manipulation" }}
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>

                    {view === "register" && (
                      <div>
                        <div
                          className={cn(
                            "relative flex items-center h-12 w-full rounded-xl border bg-secondary/30 px-4 transition-all focus-within:border-primary focus-within:bg-background",
                            passwordMismatch ? "border-destructive" : "border-border/80"
                          )}
                        >
                          <input
                            id="signup-confirm-password"
                            type={showPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(event) => {
                              const val = event.target.value;
                              setConfirmPassword(val);
                              if (passwordMismatch && password === val) {
                                setPasswordMismatch(false);
                              }
                            }}
                            placeholder="Confirmar senha"
                            className="w-full bg-transparent p-0 text-[16px] text-foreground placeholder:text-muted-foreground/60 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:ring-offset-0 focus-visible:ring-offset-0 border-none shadow-none"
                            style={{ fontSize: "16px", touchAction: "manipulation" }}
                            autoComplete="new-password"
                            required
                          />
                        </div>
                        {passwordMismatch && (
                          <p className="text-destructive text-xs font-medium mt-1.5 ml-1">
                            As senhas não conferem
                          </p>
                        )}
                      </div>
                    )}

                    {/* Primary Action Button */}
                    <Button
                      type="submit"
                      disabled={loading || (view === "register" && password !== confirmPassword)}
                      className={cn(
                        "w-full h-12 rounded-xl text-base font-semibold transition-all active:scale-[0.98] mt-2 shadow-none bg-primary text-primary-foreground hover:opacity-90",
                        loading || (view === "register" && password !== confirmPassword)
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      )}
                      style={{ touchAction: "manipulation" }}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                          <span>{view === "login" ? "Entrando..." : "Criando conta..."}</span>
                        </div>
                      ) : (
                        <span>{view === "login" ? "Entrar" : "Criar conta"}</span>
                      )}
                    </Button>
                  </form>

                  {/* Links & Switch */}
                  {view === "login" && (
                    <div className="flex flex-col items-center gap-3 pt-1 w-full">
                      <button
                        type="button"
                        className="text-sm font-semibold text-foreground hover:underline transition-all select-none"
                        style={{ touchAction: "manipulation" }}
                        onClick={onForgotPassword}
                      >
                        Esqueceu a senha?
                      </button>

                      <div
                        className="flex items-center gap-2 cursor-pointer select-none text-xs text-muted-foreground hover:text-foreground pt-1"
                        style={{ touchAction: "manipulation" }}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).tagName !== "BUTTON" && (e.target as HTMLElement).tagName !== "INPUT") {
                            setRemember((prev) => !prev);
                          }
                        }}
                      >
                        <Switch
                          id="remember-email-switch"
                          checked={remember}
                          onCheckedChange={(checked) => setRemember(!!checked)}
                          className="scale-90"
                        />
                        <label htmlFor="remember-email-switch" className="cursor-pointer font-medium select-none">
                          Lembrar meu e-mail
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom Secondary Action Button */}
                <div className="flex flex-col items-center gap-4 w-full flex-shrink-0 pt-4">
                  {view === "login" ? (
                    <button
                      type="button"
                      className="w-full h-12 rounded-xl border border-primary text-primary font-semibold text-base hover:bg-primary/10 transition-colors select-none flex items-center justify-center"
                      style={{ touchAction: "manipulation" }}
                      onClick={() => {
                        setView("register");
                        onViewChange?.("register");
                      }}
                    >
                      Criar nova conta
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="w-full h-12 rounded-xl border border-primary text-primary font-semibold text-base hover:bg-primary/10 transition-colors select-none flex items-center justify-center"
                      style={{ touchAction: "manipulation" }}
                      onClick={() => {
                        setView("login");
                        onViewChange?.("login");
                      }}
                    >
                      Já possui uma conta? Entrar
                    </button>
                  )}
                </div>
              </>
            )}

            {/* SCREEN VIEW: VERIFY 6-DIGIT OTP */}
            {view === "verify-otp" && (
              <>
                <div className="flex-1 flex flex-col justify-center items-center my-auto py-4 space-y-6 w-full text-center">
                  <div className="flex flex-col items-center justify-center w-full py-1">
                    <TicketHallLogo size="xl" className="mb-4" />
                    <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                      Código de autenticação
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-2 max-w-[290px] leading-relaxed">
                      Digite o código de 6 dígitos enviado para{" "}
                      <span className="font-semibold text-foreground block mt-0.5">{email || "seu e-mail"}</span>
                    </p>
                  </div>

                  <form onSubmit={handleOtpSubmit} className="space-y-6 w-full flex flex-col items-center" style={{ touchAction: "manipulation" }}>
                    <div className="flex justify-center my-2" style={{ touchAction: "manipulation" }}>
                      <InputOTP
                        maxLength={6}
                        value={otpCode}
                        onChange={(val) => setOtpCode(val)}
                        containerClassName="gap-2"
                      >
                        <InputOTPGroup className="gap-1.5 sm:gap-2">
                          <InputOTPSlot index={0} className="h-12 w-11 sm:h-13 sm:w-12 rounded-xl text-lg font-bold border-border bg-secondary/30" />
                          <InputOTPSlot index={1} className="h-12 w-11 sm:h-13 sm:w-12 rounded-xl text-lg font-bold border-border bg-secondary/30" />
                          <InputOTPSlot index={2} className="h-12 w-11 sm:h-13 sm:w-12 rounded-xl text-lg font-bold border-border bg-secondary/30" />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup className="gap-1.5 sm:gap-2">
                          <InputOTPSlot index={3} className="h-12 w-11 sm:h-13 sm:w-12 rounded-xl text-lg font-bold border-border bg-secondary/30" />
                          <InputOTPSlot index={4} className="h-12 w-11 sm:h-13 sm:w-12 rounded-xl text-lg font-bold border-border bg-secondary/30" />
                          <InputOTPSlot index={5} className="h-12 w-11 sm:h-13 sm:w-12 rounded-xl text-lg font-bold border-border bg-secondary/30" />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || otpCode.length < 6}
                      className={cn(
                        "w-full h-12 rounded-xl text-base font-semibold transition-all active:scale-[0.98] shadow-none bg-primary text-primary-foreground hover:opacity-90",
                        loading || otpCode.length < 6 ? "opacity-60 cursor-not-allowed" : ""
                      )}
                      style={{ touchAction: "manipulation" }}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin shrink-0" />
                          <span>Verificando...</span>
                        </div>
                      ) : (
                        <span>Confirmar código</span>
                      )}
                    </Button>
                  </form>
                </div>

                <div className="flex flex-col items-center gap-4 w-full flex-shrink-0 pt-4">
                  <button
                    type="button"
                    className="w-full h-12 rounded-xl border border-primary text-primary font-semibold text-base hover:bg-primary/10 transition-colors select-none flex items-center justify-center"
                    style={{ touchAction: "manipulation" }}
                    onClick={() => {
                      setView("login");
                      onViewChange?.("login");
                    }}
                  >
                    Voltar para o login
                  </button>

                  <div className="flex items-center justify-center opacity-70 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                    TicketHall
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
